import AdminTable from 'common/AdminTable';
import { getRequest, postRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const initialFormData = {
  instagram_link: '',
  facebook_link: '',
  youtube_link: '',
  linkedin_link: '',
  twitter_link: '',
  status: 'active'
};

const urlRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;

const SocialMedia = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest('sitesetup');
      setRecords(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load social media');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const socialFields = ['instagram_link', 'facebook_link', 'youtube_link', 'linkedin_link', 'twitter_link'];
    const invalid = socialFields.some((field) => formData[field] && !urlRegex.test(formData[field]));
    if (invalid) {
      toast.error('Please enter valid URLs only');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await patchRequest(`sitesetup/${editingId}`, formData);
        toast.success('Social media record updated successfully');
      } else {
        await postRequest('sitesetup', formData);
        toast.success('Social media record added successfully');
      }
      resetForm();
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save social media record');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setFormData({
      instagram_link: row.instagram_link || '',
      facebook_link: row.facebook_link || '',
      youtube_link: row.youtube_link || '',
      linkedin_link: row.linkedin_link || '',
      twitter_link: row.twitter_link || '',
      status: row.status || 'active'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggle = async (id) => {
    try {
      await patchRequest(`sitesetup/${id}/toggle`, {});
      toast.success('Status updated successfully');
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This social media record will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteRequest(`sitesetup/${id}`);
        toast.success('Social media record deleted successfully');
        if (editingId === id) resetForm();
        fetchRecords();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to delete social media record');
      }
    });
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    {
      Header: 'Instagram',
      accessor: 'instagram_link',
      Cell: ({ row }) =>
        row.original.instagram_link ? (
          <a href={row.original.instagram_link} target="_blank" rel="noopener noreferrer" className="text-primary">
            Open
          </a>
        ) : (
          '—'
        )
    },
    {
      Header: 'Facebook',
      accessor: 'facebook_link',
      Cell: ({ row }) =>
        row.original.facebook_link ? (
          <a href={row.original.facebook_link} target="_blank" rel="noopener noreferrer" className="text-primary">
            Open
          </a>
        ) : (
          '—'
        )
    },
    {
      Header: 'YouTube',
      accessor: 'youtube_link',
      Cell: ({ row }) =>
        row.original.youtube_link ? (
          <a href={row.original.youtube_link} target="_blank" rel="noopener noreferrer" className="text-primary">
            Open
          </a>
        ) : (
          '—'
        )
    },
    {
      Header: 'LinkedIn',
      accessor: 'linkedin_link',
      Cell: ({ row }) =>
        row.original.linkedin_link ? (
          <a href={row.original.linkedin_link} target="_blank" rel="noopener noreferrer" className="text-primary">
            Open
          </a>
        ) : (
          '—'
        )
    },
    {
      Header: 'Twitter',
      accessor: 'twitter_link',
      Cell: ({ row }) =>
        row.original.twitter_link ? (
          <a href={row.original.twitter_link} target="_blank" rel="noopener noreferrer" className="text-primary">
            Open
          </a>
        ) : (
          '—'
        )
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => (
        <button
          type="button"
          className={`btn btn-sm ${row.original.status === 'active' ? 'btn-success' : 'btn-secondary'}`}
          onClick={() => handleToggle(row.original.id)}
        >
          {row.original.status === 'active' ? 'Active' : 'Inactive'}
        </button>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn btn-sm btn-info mx-1 my-1" onClick={() => handleEdit(row.original)}>
            Edit
          </button>
          <button className="btn btn-sm btn-danger mx-1 my-1" onClick={() => handleDelete(row.original.id)}>
            Delete
          </button>
        </>
      )
    }
  ];

  return (
    <div className="container-fluid px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <h2 className="m-0">Social Media</h2>
          <small className="text-muted">Manage hospital social media links</small>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5 mb-4">
          <div className="card shadow border-0 h-100">
            <div className="card-header bg-primary text-white py-3">
              <h5 className="mb-0">{editingId ? 'Update Social Media' : 'Add Social Media'}</h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="instagram_link" className="adminlables">
                    Instagram Link
                  </label>
                  <input
                    type="text"
                    id="instagram_link"
                    name="instagram_link"
                    className="form-control"
                    placeholder="https://instagram.com/"
                    value={formData.instagram_link}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="facebook_link" className="adminlables">
                    Facebook Link
                  </label>
                  <input
                    type="text"
                    id="facebook_link"
                    name="facebook_link"
                    className="form-control"
                    placeholder="https://facebook.com/"
                    value={formData.facebook_link}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="youtube_link" className="adminlables">
                    YouTube Link
                  </label>
                  <input
                    type="text"
                    id="youtube_link"
                    name="youtube_link"
                    className="form-control"
                    placeholder="https://youtube.com/"
                    value={formData.youtube_link}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="linkedin_link" className="adminlables">
                    LinkedIn Link
                  </label>
                  <input
                    type="text"
                    id="linkedin_link"
                    name="linkedin_link"
                    className="form-control"
                    placeholder="https://linkedin.com/"
                    value={formData.linkedin_link}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="twitter_link" className="adminlables">
                    Twitter Link
                  </label>
                  <input
                    type="text"
                    id="twitter_link"
                    name="twitter_link"
                    className="form-control"
                    placeholder="https://twitter.com/"
                    value={formData.twitter_link}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="status" className="adminlables">
                    Status
                  </label>
                  <select id="status" name="status" className="form-control" value={formData.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="d-flex justify-content-end gap-3">
                  {editingId && (
                    <button type="button" className="btn btn-light px-4" onClick={resetForm}>
                      Cancel
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary px-5" disabled={saving}>
                    {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow border-0">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0">Social Media List</h5>
            </div>
            <div className="card-body p-0">
              {loading && records.length === 0 ? (
                <div className="text-center p-5">Loading...</div>
              ) : (
                <AdminTable tableHeaders={tableHeaders} tableData={records} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMedia;
